/** Split one supplier invoice between LUX Developments and LUX Construction — two copies, posted separately. */
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, Scissors, Undo2, ExternalLink } from 'lucide-react';
import { Button } from '../ui/button';
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter, AlertDialogTitle, AlertDialogDescription, AlertDialogAction, AlertDialogCancel } from '../ui/alert-dialog';
import { fmtGBP, toPence } from './LineItemsTable';
import { STATUS_LABEL } from './inboxFormat';
import { COMPANY_LABEL } from './GeneratedInvoice';
import { DeleteSplitButton } from './VoidDelete';

const COLOUR = { DEV: 'text-[#F5A623] border-[#F5A623]/40 bg-[#F5A623]/10', CON: 'text-sky-200 border-sky-400/40 bg-sky-500/10' };

/** Lines grouped by the company their job belongs to (no-project lines follow Billed to). */
export function splitGroups(lines, projCompany, billedTo) {
  const g = { DEV: [], CON: [] };
  lines.forEach(l => { g[l.project_id ? projCompany(l.project_id) : (billedTo || 'DEV')].push(l); });
  return g;
}

export function SplitButton({ api, itemId, lines, projCompany, billedTo, getPayload, vatPence, disabled, onSplit }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const groups = splitGroups(lines, projCompany, billedTo);
  const canSplit = groups.DEV.length > 0 && groups.CON.length > 0;
  const net = (ls) => ls.reduce((s, l) => s + toPence(l.amount), 0);
  const total = net(lines);
  const vatShare = (ls) => (vatPence == null || !total ? null : Math.round(vatPence * net(ls) / total));
  const run = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/cost-inbox/${itemId}/split`, getPayload());
      toast.success('Split into two copies — post each one to its company');
      setOpen(false);
      onSplit?.(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not split the invoice'); }
    finally { setBusy(false); }
  };
  return (
    <>
      <Button variant="outline" onClick={() => setOpen(true)} disabled={disabled || !canSplit}
        title={canSplit ? 'Make one copy per company, each with its own lines and share of the VAT' : 'Needs at least one line on a LUX Developments job and one on a LUX Construction job'}
        className="border-sky-400/40 text-sky-200 bg-sky-500/10 hover:bg-sky-500/20" data-testid="inbox-split-btn">
        <Scissors className="w-4 h-4 mr-1.5" /> Split by company
      </Button>
      <AlertDialog open={open} onOpenChange={setOpen}>
        <AlertDialogContent className="bg-[#1E2530] border-white/10 text-white" data-testid="inbox-split-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2"><Scissors className="w-5 h-5 text-sky-300" /> Split this invoice between the two companies?</AlertDialogTitle>
            <AlertDialogDescription className="text-gray-400">
              Two editable copies are created with the same original attached. Each keeps the supplier's invoice number, takes only its company's lines and a pro-rata share of the VAT, and goes to Xero / LiveCosts as a generated invoice showing just that share. You then post each copy separately.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="grid grid-cols-2 gap-2 text-xs">
            {['DEV', 'CON'].map(c => (
              <div key={c} className={`rounded-lg border p-2.5 ${COLOUR[c]}`} data-testid={`inbox-split-part-${c}`}>
                <div className="font-semibold">{COMPANY_LABEL[c]}</div>
                <div className="text-gray-300 mt-1">{groups[c].length} line{groups[c].length === 1 ? '' : 's'}</div>
                <div className="text-white font-semibold tabular-nums mt-0.5">{fmtGBP(net(groups[c]))} <span className="font-normal text-gray-400">ex VAT</span></div>
                {vatShare(groups[c]) != null && <div className="text-gray-400 tabular-nums">VAT share {fmtGBP(vatShare(groups[c]))}</div>}
              </div>
            ))}
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-white/5 border-white/20 text-white hover:bg-white/10" data-testid="inbox-split-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={(e) => { e.preventDefault(); run(); }} disabled={busy} className="bg-sky-500 text-white hover:bg-sky-400" data-testid="inbox-split-confirm-btn">
              {busy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Scissors className="w-4 h-4 mr-1.5" />} Split into 2 copies
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

/** Banner on a split COPY: which share this is, with links to the other copy and the original. */
export function SplitChildBanner({ item, onOpenItem }) {
  const sp = item?.split;
  if (!sp) return null;
  const other = sp.part === 'CON' ? 'DEV' : 'CON';
  return (
    <div className={`rounded-lg border p-2.5 text-xs flex items-start gap-2 ${COLOUR[sp.part]}`} data-testid="inbox-split-child-banner">
      <Scissors className="w-4 h-4 mt-0.5 shrink-0" />
      <div className="min-w-0 text-gray-200">
        <span className="font-semibold text-white">Split copy {sp.part_no} of 2 — {COMPANY_LABEL[sp.part]} share.</span>{' '}
        {fmtGBP(item.net_pence || 0)} ex VAT of the supplier's {fmtGBP(sp.parent_net_pence || 0)} total{sp.parent_reference ? ` (ref ${sp.parent_reference})` : ''}.
        {onOpenItem && (
          <span className="ml-1.5 inline-flex gap-2">
            <button type="button" onClick={() => onOpenItem(sp.sibling_id)} className="underline hover:text-white inline-flex items-center gap-1" data-testid="inbox-split-open-sibling">{COMPANY_LABEL[other]} copy <ExternalLink className="w-3 h-3" /></button>
            <button type="button" onClick={() => onOpenItem(sp.parent_id)} className="underline hover:text-white" data-testid="inbox-split-open-parent">original</button>
          </span>
        )}
      </div>
    </div>
  );
}

/** Banner on the split PARENT: the two copies with status, open links and Undo. */
export function SplitParentBanner({ api, item, onOpenItem, onChanged, onDeleted }) {
  const ids = item?.split_children || [];
  const [kids, setKids] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (item?.status !== 'split' || !ids.length) return undefined;
    let alive = true;
    Promise.all(ids.map(id => api.get(`/cost-inbox/${id}`).then(r => r.data).catch(() => null))).then(rows => { if (alive) setKids(rows.filter(Boolean)); });
    return () => { alive = false; };
  }, [api, item?.status, ids.join(',')]); // eslint-disable-line react-hooks/exhaustive-deps
  if (item?.status !== 'split') return null;
  const undo = async () => {
    if (!window.confirm('Undo the split? Both copies are removed and this invoice re-opens for processing.')) return;
    setBusy(true);
    try { await api.post(`/cost-inbox/${item.id}/unsplit`); toast.success('Split undone'); onChanged?.(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Could not undo the split'); }
    finally { setBusy(false); }
  };
  const canUndo = kids.length > 0 && kids.every(k => k.status !== 'posted');
  return (
    <div className="rounded-lg border border-sky-400/40 bg-sky-500/10 p-2.5 text-xs" data-testid="inbox-split-parent-banner">
      <div className="flex items-center gap-2 text-sky-100">
        <Scissors className="w-4 h-4 shrink-0" />
        <span className="font-semibold text-white">Split between the two companies</span>
        <span className="text-gray-300">{item.split_at ? new Date(item.split_at).toLocaleString('en-GB') : ''}{item.split_by_name ? ` by ${item.split_by_name}` : ''} · post each copy to its own company</span>
        <span className="flex-1" />
        {canUndo && <button type="button" onClick={undo} disabled={busy} className="inline-flex items-center gap-1 underline hover:text-white disabled:opacity-50" data-testid="inbox-unsplit-btn"><Undo2 className="w-3.5 h-3.5" /> Undo split</button>}
        <DeleteSplitButton api={api} item={item} kids={kids} onDone={onDeleted} />
      </div>
      <div className="mt-2 grid sm:grid-cols-2 gap-2">
        {kids.map(k => (
          <button key={k.id} type="button" onClick={() => onOpenItem?.(k.id)} className={`text-left rounded-lg border p-2.5 hover:brightness-110 ${COLOUR[k.split?.part] || ''}`} data-testid={`inbox-split-kid-${k.split?.part}`}>
            <div className="flex items-center justify-between gap-2">
              <span className="font-semibold">{COMPANY_LABEL[k.split?.part] || 'Copy'}</span>
              <span className="text-[10px] px-1.5 py-0.5 rounded-full border border-white/20 text-gray-200">{STATUS_LABEL[k.status] || k.status}</span>
            </div>
            <div className="text-gray-200 mt-1 tabular-nums">{fmtGBP(k.net_pence || 0)} ex VAT · {(k.lines || []).length} line{(k.lines || []).length === 1 ? '' : 's'}</div>
          </button>
        ))}
      </div>
    </div>
  );
}
